// src/components/TrophyRoom/TopPerformersPodium.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Avatar, 
  Fade, 
  Grow, 
  useMediaQuery, 
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import { 
  EmojiEvents as TrophyIcon,
  Star as StarIcon 
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

// Animation keyframes
const podiumRise = keyframes`
  0% {
    height: 0;
    opacity: 0;
  }
  100% {
    height: var(--target-height);
    opacity: 1;
  }
`;

const avatarBounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
`;

const crownFloat = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-3px) rotate(1deg);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

function TopPerformersPodium({ leaderboard }) {
  const [animationStarted, setAnimationStarted] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  
  const top3 = leaderboard.slice(0, 3);
  
  useEffect(() => {
    if (top3.length > 0) {
      const timer = setTimeout(() => setAnimationStarted(true), 300);
      return () => clearTimeout(timer);
    }
  }, [top3.length]);

  if (top3.length === 0) return null;

  const getPodiumHeight = (rank) => {
    if (isMobile) {
      if (rank === 1) return 100;
      if (rank === 2) return 80;
      if (rank === 3) return 60;
      return 40;
    }
    if (rank === 1) return 140;
    if (rank === 2) return 110;
    if (rank === 3) return 80;
    return 60;
  };

  const getPodiumColor = (rank) => {
    if (rank === 1) return 'linear-gradient(135deg, #FFD700 0%, #FFA000 50%, #FF8F00 100%)';
    if (rank === 2) return 'linear-gradient(135deg, #E8E8E8 0%, #C0C0C0 50%, #B0B0B0 100%)';
    if (rank === 3) return 'linear-gradient(135deg, #CD7F32 0%, #B8860B 50%, #8B4513 100%)';
    return 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)';
  };

  const getDisplayInfo = (user) => {
    if (user.is_private) {
      return {
        displayName: user.display_name,
        avatar: null,
        avatarText: 'A'
      };
    }
    return {
      displayName: user.display_name || user.username,
      avatar: user.profile_pic,
      avatarText: user.display_name?.charAt(0) || user.username?.charAt(0)
    };
  };

  // Mobile Card Layout for very small screens
  if (isMobile && window.innerWidth < 400) {
    return (
      <Box sx={{ mb: { xs: 4, sm: 6 }, px: { xs: 1, sm: 2 } }}>
        <Fade in timeout={800}>
          <Typography 
            variant="h5" 
            textAlign="center" 
            mb={3} 
            fontWeight="bold"
            sx={{
              background: 'linear-gradient(45deg, #FFD700, #FFA000)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            🏆 Top Performers
          </Typography>
        </Fade>
        
        <Stack spacing={2}>
          {top3.map((user, index) => {
            const rank = index + 1;
            const info = getDisplayInfo(user);
            const isFirst = rank === 1;
            
            return (
              <Grow key={user.global_user_id} in={animationStarted} timeout={800 + (index * 200)}>
                <Card sx={{
                  background: isFirst 
                    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 160, 0, 0.05))' 
                    : 'background.paper',
                  border: `2px solid ${rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32'}`,
                  borderRadius: 3,
                  boxShadow: isFirst ? '0 8px 25px rgba(255, 215, 0, 0.3)' : '0 4px 15px rgba(0,0,0,0.1)'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="h4" fontWeight="900" sx={{
                        color: rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32',
                        minWidth: '40px',
                        textAlign: 'center'
                      }}>
                        {rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉'}
                      </Typography>
                      
                      <Avatar 
                        src={info.avatar} 
                        sx={{ 
                          width: 48, 
                          height: 48,
                          border: `3px solid ${rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32'}`,
                          bgcolor: user.is_private ? 'grey.400' : 'primary.main',
                          opacity: user.is_private ? 0.7 : 1
                        }}
                      >
                        {info.avatarText}
                      </Avatar>
                      
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{
                          opacity: user.is_private ? 0.7 : 1,
                          fontStyle: user.is_private ? 'italic' : 'normal'
                        }}>
                          {info.displayName}
                        </Typography>
                        <Chip 
                          label={`${user.total_points.toLocaleString()} pts`}
                          size="small"
                          color={isFirst ? "warning" : "default"}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>

                      {isFirst && (
                        <TrophyIcon sx={{ 
                          color: '#FFD700', 
                          fontSize: 32,
                          animation: animationStarted ? `${crownFloat} 3s ease-in-out 2s infinite` : 'none'
                        }} />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            );
          })}
        </Stack>
      </Box>
    );
  }

  // Standard Podium Layout
  return (
    <Box sx={{ mb: { xs: 4, sm: 6 }, px: { xs: 1, sm: 2, md: 4 } }}>
      <Fade in timeout={800}>
        <Typography 
          variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
          textAlign="center" 
          mb={{ xs: 3, sm: 4 }} 
          fontWeight="bold"
          sx={{
            background: 'linear-gradient(45deg, #FFD700, #FFA000)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 2px 10px rgba(255, 215, 0, 0.3)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.1), transparent)',
              backgroundSize: '200% 100%',
              animation: `${shimmer} 3s ease-in-out infinite`,
              borderRadius: 2,
              zIndex: -1
            }
          }}
        >
          {isMobile ? '🏆 Champions' : '🏆 Champion\'s Podium'}
        </Typography>
      </Fade>
      
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="end" 
        gap={{ xs: 1, sm: 2, md: 3 }}
        sx={{ 
          perspective: '1000px',
          minHeight: { xs: '200px', sm: '280px' }
        }}
      >
        {top3.map((user, index) => {
          const rank = index + 1;
          const info = getDisplayInfo(user);
          const isFirst = rank === 1;
          
          return (
            <Grow key={user.global_user_id} in={animationStarted} timeout={1000 + (index * 200)}>
              <Box textAlign="center" sx={{ 
                transform: isFirst ? 'none' : rank === 2 ? 'rotateY(-3deg)' : 'rotateY(3deg)',
                zIndex: isFirst ? 3 : rank === 2 ? 2 : 1,
                position: 'relative'
              }}>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <Avatar 
                    src={info.avatar} 
                    sx={{ 
                      width: { 
                        xs: isFirst ? 64 : 48, 
                        sm: isFirst ? 80 : 60, 
                        md: isFirst ? 90 : 70 
                      }, 
                      height: { 
                        xs: isFirst ? 64 : 48, 
                        sm: isFirst ? 80 : 60, 
                        md: isFirst ? 90 : 70 
                      },
                      mx: 'auto',
                      border: `${isFirst ? 4 : 3}px solid ${rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32'}`,
                      bgcolor: user.is_private ? 'grey.400' : 'primary.main',
                      opacity: user.is_private ? 0.7 : 1,
                      boxShadow: `0 ${isFirst ? 8 : 6}px ${isFirst ? 25 : 20}px ${
                        rank === 1 ? 'rgba(255, 215, 0, 0.4)' : 
                        rank === 2 ? 'rgba(192, 192, 192, 0.3)' : 
                        'rgba(205, 127, 50, 0.3)'
                      }`,
                      animation: animationStarted ? `${avatarBounce} 2s ease-in-out ${1 + index * 0.2}s` : 'none',
                      position: 'relative',
                      overflow: 'visible',
                      '&::before': isFirst ? {
                        content: '""',
                        position: 'absolute',
                        top: -2,
                        left: -2,
                        right: -2,
                        bottom: -2,
                        background: 'linear-gradient(45deg, #FFD700, #FFA000, #FFD700)',
                        borderRadius: '50%',
                        zIndex: -1,
                        animation: `${shimmer} 2s linear infinite`
                      } : {}
                    }}
                  >
                    {info.avatarText}
                  </Avatar>
                  {isFirst && (
                    <TrophyIcon 
                      sx={{ 
                        position: 'absolute', 
                        top: { xs: -8, sm: -10, md: -12 }, 
                        right: { xs: -8, sm: -10, md: -12 }, 
                        color: '#FFD700',
                        fontSize: { xs: 28, sm: 36, md: 42 },
                        background: 'white',
                        borderRadius: '50%',
                        p: 0.5,
                        boxShadow: '0 4px 15px rgba(255, 215, 0, 0.5)',
                        animation: animationStarted ? `${crownFloat} 3s ease-in-out 2s infinite` : 'none',
                        border: '2px solid #FFD700'
                      }} 
                    />
                  )}
                </Box>
                
                <Box sx={{ 
                  minHeight: { xs: '50px', sm: '60px' }, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center', 
                  mb: 2,
                  maxWidth: { xs: '80px', sm: '120px' },
                  mx: 'auto'
                }}>
                  <Typography 
                    variant={isFirst ? (isMobile ? "subtitle1" : "h6") : (isMobile ? "body2" : "subtitle1")} 
                    fontWeight="bold"
                    sx={{ 
                      opacity: user.is_private ? 0.7 : 1,
                      fontStyle: user.is_private ? 'italic' : 'normal',
                      background: isFirst ? 'linear-gradient(45deg, #FFD700, #FFA000)' : 'none',
                      backgroundClip: isFirst ? 'text' : 'none',
                      WebkitBackgroundClip: isFirst ? 'text' : 'none',
                      WebkitTextFillColor: isFirst ? 'transparent' : 'inherit',
                      fontSize: { 
                        xs: isFirst ? '0.95rem' : '0.8rem', 
                        sm: isFirst ? '1.1rem' : '0.95rem',
                        md: isFirst ? '1.25rem' : '1rem' 
                      },
                      lineHeight: 1.2,
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      hyphens: 'auto'
                    }}
                  >
                    {info.displayName}
                  </Typography>
                  <Typography 
                    variant={isFirst ? (isMobile ? "caption" : "body2") : "caption"} 
                    color={isFirst ? "warning.main" : "text.secondary"}
                    fontWeight={isFirst ? "bold" : "600"}
                    sx={{ 
                      mt: 0.5,
                      fontSize: { 
                        xs: isFirst ? '0.75rem' : '0.7rem',
                        sm: isFirst ? '0.875rem' : '0.75rem'
                      }
                    }}
                  >
                    {user.total_points.toLocaleString()} pts
                  </Typography>
                </Box>
                
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: { 
                      xs: isFirst ? 70 : rank === 2 ? 60 : 50, 
                      sm: isFirst ? 100 : rank === 2 ? 80 : 70,
                      md: isFirst ? 120 : rank === 2 ? 100 : 80 
                    },
                    mx: 'auto',
                    '--target-height': `${getPodiumHeight(rank)}px`,
                    height: animationStarted ? getPodiumHeight(rank) : 0,
                    background: getPodiumColor(rank),
                    borderRadius: { xs: '8px 8px 0 0', sm: '12px 12px 0 0' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { 
                      xs: isFirst ? '1.2rem' : '0.9rem', 
                      sm: isFirst ? '1.8rem' : '1.2rem',
                      md: isFirst ? '2.2rem' : '1.5rem' 
                    },
                    boxShadow: `0 ${isFirst ? 8 : 6}px ${isFirst ? 30 : 20}px rgba(0,0,0,0.${isFirst ? 3 : 2})`,
                    animation: animationStarted ? `${podiumRise} 1.5s ease-out ${0.3 + index * 0.2}s both` : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: animationStarted ? `${shimmer} 3s ease-in-out ${2 + index * 0.3}s infinite` : 'none'
                    }
                  }}
                >
                  {rank}
                </Box>
              </Box>
            </Grow>
          );
        })}
      </Box>
    </Box>
  );
}

export default TopPerformersPodium;